import axios from 'axios';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import BusinessMetrics from '../components/BusinessMetrics';
import PropositionScoring from '../components/PropositionScoring';
import ResearchTab from '../components/ResearchTab';
import { ResearchEntityType } from '../services/researchService';
import { useOrganization } from '../context/OrganizationContext';
import { groupsService } from '../services/groups';
import { GroupSummary } from '../types/groups';
import { formatCurrency as formatCurrencyUtil } from '../utils/currency';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import { masterPlanTaskService, MasterPlanTask } from '../services/masterPlan';
import { okrService, OrganizationOKR } from '../services/okrs';
import {
  MarkdownEditor,
  MarkdownViewer,
  MultiEntityMentionsEditorRef,
} from '../components/markdown';
import {
  formatPropositionType,
  formatCurrentCapability,
  formatPricingType,
  formatPricingFrequency,
  formatUserTypeImportance,
  formatRoleType,
} from '../utils/enumFormatting';
import { API_URL } from '../constants/urls';

type Proposition = {
  id: string;
  name: string;
  type?: string;
  description?: string;
  current_capability?: string;
  owner_id?: string;
  owner_name?: string;
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;

  // Business metrics
  prospect_count?: number;
  customer_count?: number;
  testimonial_count?: number;
  case_study_count?: number;
  award_count?: number;

  // Enhanced fields
  problems_solved?: string[];
  value_delivered?: string[];
  jobs_to_be_done?: string[];
  competitive_advantages?: string[];
  success_metrics?: string[];

  // User types and roles
  user_types_roles?: any[];

  // Pricing models
  pricing_models?: any[];

  // Group
  group_id?: string;
  group?: { id: string; name: string; color: string | null } | null;
};

interface EditableListFieldProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

function EditableListField({
  label,
  items = [],
  onChange,
  placeholder = 'Add new item...',
}: EditableListFieldProps) {
  const [newItem, setNewItem] = useState('');

  const handleAdd = () => {
    if (newItem.trim()) {
      onChange([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleRemove = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    onChange(updatedItems);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {items.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
            >
              {item}
              <button
                onClick={() => handleRemove(index)}
                className="ml-2 hover:text-red-600"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleAdd}
          disabled={!newItem.trim()}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export default function PropositionDetailTailwind() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, canEdit } = useOrganization();

  // Ref for markdown editor
  const descriptionRef = useRef<MultiEntityMentionsEditorRef>(null);

  // Function to get initial tab from URL hash
  const getInitialTab = (): 'overview' | 'tasks' | 'research' => {
    const hash = location.hash.replace('#', '');
    if (hash === 'tasks' || hash === 'research') {
      return hash;
    }
    return 'overview';
  };

  const [prop, setProp] = useState<Proposition | null>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [editingProposition, setEditingProposition] = useState(false);
  const [propositionForm, setPropositionForm] = useState({
    name: '',
    type: '',
    description: '',
    current_capability: '',
    owner_id: '',
    group_id: '',
    problems_solved: [] as string[],
    value_delivered: [] as string[],
    jobs_to_be_done: [] as string[],
    competitive_advantages: [] as string[],
    success_metrics: [] as string[],
  });

  // User Types state
  const [showUserTypeForm, setShowUserTypeForm] = useState(false);
  const [editingUserType, setEditingUserType] = useState<any>(null);
  const [userTypeForm, setUserTypeForm] = useState({
    role_type: '',
    user_type: '',
    description: '',
    importance: '',
  });

  // Pricing Models state
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState<any>(null);
  const [pricingForm, setPricingForm] = useState({
    name: '',
    description: '',
    is_default: false,
    price: '',
    currency: '',
    pricing_type: '',
    frequency: '',
    geographic_markets: [] as string[],
    industry_verticals: [] as string[],
  });

  // State for tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'research'>(
    getInitialTab(),
  );
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [taskViewMode, setTaskViewMode] = useState<'kanban' | 'list'>('kanban');
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [allOKRs, setAllOKRs] = useState<OrganizationOKR[]>([]);

  // Function to handle tab changes and update URL
  const handleTabChange = (tab: 'overview' | 'tasks' | 'research') => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  // Track previous organization to detect changes
  const prevOrgRef = React.useRef<string | null>(null);

  // Populate form when entering edit mode
  useEffect(() => {
    if (editingProposition && prop) {
      setPropositionForm({
        name: prop.name || '',
        type: prop.type || '',
        description: prop.description || '',
        current_capability: prop.current_capability || '',
        owner_id: prop.owner_id || '',
        group_id: prop.group_id || '',
        problems_solved: prop.problems_solved || [],
        value_delivered: prop.value_delivered || [],
        jobs_to_be_done: prop.jobs_to_be_done || [],
        competitive_advantages: prop.competitive_advantages || [],
        success_metrics: prop.success_metrics || [],
      });
    }
  }, [editingProposition, prop]);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'tasks' || hash === 'research') {
        setActiveTab(hash);
      } else {
        setActiveTab('overview');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (!id) return;

    if (!selectedOrg) {
      // Organization not loaded yet, will retry when it loads
      return;
    }

    // If organization changed, navigate to propositions list
    if (
      prevOrgRef.current &&
      selectedOrg.id &&
      prevOrgRef.current !== selectedOrg.id
    ) {
      // Organization changed logging removed
      navigate('/propositions');
      return;
    }

    // Update the ref for next render
    if (selectedOrg.id) {
      prevOrgRef.current = selectedOrg.id;
    }
    const savedOrgId = selectedOrg.id;

    // Fetch the full organization details to get currency
    axios
      .get(`${API_URL}/organizations/${savedOrgId}`)
      .then((res) => setOrganization(res.data))
      .catch((_err) => {
        // Error fetching organization
      });

    // Fetch proposition
    axios
      .get(`${API_URL}/propositions/${id}?organization_id=${savedOrgId}`)
      .then((res) => {
        setProp(res.data);
        setPropositionForm({
          name: res.data.name || '',
          type: res.data.type || '',
          description: res.data.description || '',
          current_capability: res.data.current_capability || '',
          owner_id: res.data.owner_id || '',
          group_id: res.data.group_id || '',
          problems_solved: res.data.problems_solved || [],
          value_delivered: res.data.value_delivered || [],
          jobs_to_be_done: res.data.jobs_to_be_done || [],
          competitive_advantages: res.data.competitive_advantages || [],
          success_metrics: res.data.success_metrics || [],
        });
      })
      .catch((_err) => {
        // Error fetching proposition
      });

    // Fetch organization members for owner selection
    axios
      .get(
        `${API_URL}/organizations/${savedOrgId}/members?organization_id=${savedOrgId}`,
      )
      .then((res) => setMembers(res.data))
      .catch((_err) => {
        // Error fetching members
      });

    // Fetch groups
    groupsService
      .getGroupsSummary(savedOrgId, 'proposition')
      .then(setGroups)
      .catch((_err) => {
        // Error fetching groups summary
      });

    // Fetch tasks for this proposition
    fetchTasks(savedOrgId);

    // Fetch OKRs for task alignment
    fetchOKRs(savedOrgId);

    // Fetch organization members for task assignment
    axios
      .get(
        `${API_URL}/organizations/${savedOrgId}/members?organization_id=${savedOrgId}`,
      )
      .then((res) => setOrganizationMembers(res.data))
      .catch((_err) => {
        // Error fetching organization members
      });
  }, [id, selectedOrg, navigate]);

  // Task-related functions
  const fetchTasks = async (orgId: string) => {
    if (!id) return;
    try {
      console.log('Fetching tasks for proposition:', id);
      const fetchedTasks = await masterPlanTaskService.list(orgId, {
        proposition_ids: [id],
        include_org_tasks: false, // Only show proposition-specific tasks
      });
      console.log('Fetched tasks:', fetchedTasks);
      // Log details about the tasks
      fetchedTasks.forEach((task) => {
        console.log(
          `Task: ${task.title}, context_type: ${task.context_type}, context_object_id: ${task.context_object_id}`,
        );
      });
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchOKRs = async (orgId: string) => {
    try {
      const okrs = await okrService.listOKRs(orgId);
      // Filter for only draft and active OKRs
      const activeOKRs = okrs.filter(
        (okr) => okr.status === 'draft' || okr.status === 'active',
      );
      setAllOKRs(activeOKRs);
    } catch (error) {
      console.error('Error fetching OKRs:', error);
      setAllOKRs([]);
    }
  };

  const handleTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.update(selectedOrg.id, taskId, updates);
      await fetchTasks(selectedOrg.id);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskCreate = async (task: MasterPlanTask) => {
    // TaskModal already creates the task, just refresh the list
    if (!selectedOrg) return;
    await fetchTasks(selectedOrg.id);
  };

  const handleTaskDelete = async (taskId: string) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.delete(selectedOrg.id, taskId);
      await fetchTasks(selectedOrg.id);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleSaveProposition = async () => {
    if (!prop || !selectedOrg) return;

    try {
      // Get tokenized content from markdown editor
      const tokenizedDescription =
        descriptionRef.current?.getTokenizedContent() ||
        propositionForm.description;

      // Clean the form data - convert empty strings to null/undefined for optional fields
      const cleanedData = {
        name: propositionForm.name,
        type: propositionForm.type || undefined,
        description: tokenizedDescription || undefined,
        current_capability: propositionForm.current_capability || undefined,
        owner_id: propositionForm.owner_id || null, // Use null for owner_id to clear it
        group_id: propositionForm.group_id || undefined,
        problems_solved: propositionForm.problems_solved,
        value_delivered: propositionForm.value_delivered,
        jobs_to_be_done: propositionForm.jobs_to_be_done,
        competitive_advantages: propositionForm.competitive_advantages,
        success_metrics: propositionForm.success_metrics,
      };

      // Remove owner_id if it's an empty string
      if (cleanedData.owner_id === '') {
        cleanedData.owner_id = null;
      }

      const response = await axios.put(
        `${API_URL}/propositions/${prop.id}?organization_id=${selectedOrg.id}`,
        cleanedData,
      );
      setProp(response.data);
      setEditingProposition(false);
    } catch (error) {
      // console.error removed
    }
  };

  const handleSaveUserType = async () => {
    if (!prop || !selectedOrg) return;

    try {
      if (editingUserType) {
        // Update existing
        const response = await axios.put(
          `${API_URL}/propositions/${prop.id}/user-types-roles/${editingUserType.id}?organization_id=${selectedOrg.id}`,
          userTypeForm,
        );

        setProp({
          ...prop,
          user_types_roles: prop.user_types_roles?.map((utr) =>
            utr.id === editingUserType.id ? response.data : utr,
          ),
        });
      } else {
        // Create new
        const response = await axios.post(
          `${API_URL}/propositions/${prop.id}/user-types-roles?organization_id=${selectedOrg.id}`,
          userTypeForm,
        );

        setProp({
          ...prop,
          user_types_roles: [...(prop.user_types_roles || []), response.data],
        });
      }

      // Reset form
      setUserTypeForm({
        role_type: '',
        user_type: '',
        description: '',
        importance: '',
      });
      setEditingUserType(null);
      setShowUserTypeForm(false);
    } catch (error) {
      // console.error removed
    }
  };

  const handleDeleteUserType = async (userTypeId: string) => {
    if (
      !prop ||
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this user type?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/propositions/${prop.id}/user-types-roles/${userTypeId}?organization_id=${selectedOrg.id}`,
      );

      setProp({
        ...prop,
        user_types_roles: prop.user_types_roles?.filter(
          (utr) => utr.id !== userTypeId,
        ),
      });
    } catch (error) {
      // console.error removed
    }
  };

  const handleSavePricingModel = async () => {
    if (!prop || !selectedOrg) return;

    try {
      const cleanedData: any = {
        name: pricingForm.name,
        description: pricingForm.description || undefined,
        is_default: pricingForm.is_default,
        price: pricingForm.price ? parseFloat(pricingForm.price) : undefined,
        currency: pricingForm.currency || organization?.currency || 'USD',
        pricing_type: pricingForm.pricing_type || undefined,
        frequency: pricingForm.frequency || undefined,
        geographic_markets:
          pricingForm.geographic_markets.length > 0
            ? pricingForm.geographic_markets
            : [],
        industry_verticals:
          pricingForm.industry_verticals.length > 0
            ? pricingForm.industry_verticals
            : [],
      };

      // Remove undefined values
      Object.keys(cleanedData).forEach((key) => {
        if (cleanedData[key] === undefined) {
          delete cleanedData[key];
        }
      });

      // Debug: // console.log removed

      if (editingPricing) {
        // Update existing
        const response = await axios.put(
          `${API_URL}/propositions/${prop.id}/pricing/${editingPricing.id}?organization_id=${selectedOrg.id}`,
          cleanedData,
        );

        setProp({
          ...prop,
          pricing_models: prop.pricing_models?.map((pm) =>
            pm.id === editingPricing.id ? response.data : pm,
          ),
        });
      } else {
        // Create new
        const response = await axios.post(
          `${API_URL}/propositions/${prop.id}/pricing?organization_id=${selectedOrg.id}`,
          cleanedData,
        );

        setProp({
          ...prop,
          pricing_models: [...(prop.pricing_models || []), response.data],
        });
      }

      // Reset form
      setPricingForm({
        name: '',
        description: '',
        is_default: false,
        price: '',
        currency: '',
        pricing_type: '',
        frequency: '',
        geographic_markets: [],
        industry_verticals: [],
      });
      setEditingPricing(null);
      setShowPricingForm(false);
    } catch (error: any) {
      // console.error removed
      // Log more detail for debugging
      if (error.response) {
        // console.error removed
        alert(
          `Error saving pricing model: ${error.response.data.detail || error.response.data.message || 'Unknown error'}`,
        );
      } else {
        alert(
          `Error saving pricing model: ${error.message || 'Unknown error'}`,
        );
      }
    }
  };

  const handleDeletePricingModel = async (pricingId: string) => {
    if (
      !prop ||
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this pricing model?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/propositions/${prop.id}/pricing/${pricingId}?organization_id=${selectedOrg.id}`,
      );

      setProp({
        ...prop,
        pricing_models: prop.pricing_models?.filter(
          (pm) => pm.id !== pricingId,
        ),
      });
    } catch (error) {
      // console.error removed
    }
  };

  const formatCurrency = (value?: number) => {
    const currencyCode = organization?.currency || 'USD';
    return formatCurrencyUtil(value, currencyCode, {
      compact: true,
      maximumFractionDigits: 1,
    });
  };

  if (!prop) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading proposition details...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/propositions')}
          className="mr-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="text-3xl font-bold flex-grow">{prop.name}</h1>
        {canEdit && (
          <div className="flex gap-2">
            {editingProposition ? (
              <>
                <button
                  onClick={() => setEditingProposition(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProposition}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditingProposition(true)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Edit Proposition
              </button>
            )}
          </div>
        )}
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => handleTabChange('research')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'research'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Research
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Business Metrics Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Business Metrics</h2>
            <BusinessMetrics
              prospectCount={prop.prospect_count}
              customerCount={prop.customer_count}
              testimonialCount={prop.testimonial_count}
              caseStudyCount={prop.case_study_count}
              awardCount={prop.award_count}
              organizationId={selectedOrg?.id || ''}
              propositionId={id}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Basic Information
                </h3>
                {editingProposition ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={propositionForm.name}
                        onChange={(e) =>
                          setPropositionForm({
                            ...propositionForm,
                            name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={propositionForm.type}
                        onChange={(e) =>
                          setPropositionForm({
                            ...propositionForm,
                            type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select type...</option>
                        <option value="product">Product</option>
                        <option value="service">Service</option>
                        <option value="solution">Solution</option>
                        <option value="capability">Capability</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <MarkdownEditor
                        ref={descriptionRef}
                        value={propositionForm.description}
                        onChange={(value) =>
                          setPropositionForm({
                            ...propositionForm,
                            description: value,
                          })
                        }
                        placeholder="Describe the proposition..."
                        organizationId={selectedOrg?.id || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Capability
                      </label>
                      <select
                        value={propositionForm.current_capability}
                        onChange={(e) =>
                          setPropositionForm({
                            ...propositionForm,
                            current_capability: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select capability level...</option>
                        <option value="idea">Idea</option>
                        <option value="concept">Concept</option>
                        <option value="foundation">Foundation</option>
                        <option value="developing">Developing</option>
                        <option value="proven">Proven</option>
                        <option value="mature">Mature</option>
                        <option value="retiring">Retiring</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner
                      </label>
                      <select
                        value={propositionForm.owner_id}
                        onChange={(e) =>
                          setPropositionForm({
                            ...propositionForm,
                            owner_id: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not assigned</option>
                        {members.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.user_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Group
                      </label>
                      <select
                        value={propositionForm.group_id}
                        onChange={(e) =>
                          setPropositionForm({
                            ...propositionForm,
                            group_id: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No group</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p>
                      <span className="font-medium">Type:</span>{' '}
                      {formatPropositionType(prop.type) || 'Not specified'}
                    </p>
                    <div>
                      <span className="font-medium">Description:</span>
                      {prop.description ? (
                        <div className="mt-1">
                          <MarkdownViewer content={prop.description} />
                        </div>
                      ) : (
                        <span className="text-gray-500 ml-1">
                          No description
                        </span>
                      )}
                    </div>
                    <p>
                      <span className="font-medium">Current Capability:</span>{' '}
                      {formatCurrentCapability(prop.current_capability) ||
                        'Not specified'}
                    </p>
                    <p>
                      <span className="font-medium">Owner:</span>{' '}
                      {prop.owner_name || 'Not assigned'}
                    </p>
                    {prop.group && (
                      <p>
                        <span className="font-medium">Group:</span>{' '}
                        <span
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: prop.group.color
                              ? `${prop.group.color}20`
                              : '#f3f4f6',
                            color: prop.group.color || '#374151',
                          }}
                        >
                          {prop.group.name}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Value Proposition Canvas */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Value Proposition Canvas
                </h3>
                {editingProposition ? (
                  <div className="space-y-4">
                    <EditableListField
                      label="Problems Solved"
                      items={propositionForm.problems_solved}
                      onChange={(items) =>
                        setPropositionForm({
                          ...propositionForm,
                          problems_solved: items,
                        })
                      }
                      placeholder="Add a problem this solves..."
                    />
                    <EditableListField
                      label="Value Delivered"
                      items={propositionForm.value_delivered}
                      onChange={(items) =>
                        setPropositionForm({
                          ...propositionForm,
                          value_delivered: items,
                        })
                      }
                      placeholder="Add value delivered..."
                    />
                    <EditableListField
                      label="Jobs to be Done"
                      items={propositionForm.jobs_to_be_done}
                      onChange={(items) =>
                        setPropositionForm({
                          ...propositionForm,
                          jobs_to_be_done: items,
                        })
                      }
                      placeholder="Add a job to be done..."
                    />
                    <EditableListField
                      label="Competitive Advantages"
                      items={propositionForm.competitive_advantages}
                      onChange={(items) =>
                        setPropositionForm({
                          ...propositionForm,
                          competitive_advantages: items,
                        })
                      }
                      placeholder="Add a competitive advantage..."
                    />
                    <EditableListField
                      label="Success Metrics"
                      items={propositionForm.success_metrics}
                      onChange={(items) =>
                        setPropositionForm({
                          ...propositionForm,
                          success_metrics: items,
                        })
                      }
                      placeholder="Add a success metric..."
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Problems Solved */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Problems Solved
                      </h4>
                      {prop.problems_solved &&
                      prop.problems_solved.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {prop.problems_solved.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No problems defined
                        </p>
                      )}
                    </div>

                    {/* Value Delivered */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Value Delivered
                      </h4>
                      {prop.value_delivered &&
                      prop.value_delivered.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {prop.value_delivered.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No value propositions defined
                        </p>
                      )}
                    </div>

                    {/* Jobs to be Done */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Jobs to be Done
                      </h4>
                      {prop.jobs_to_be_done &&
                      prop.jobs_to_be_done.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {prop.jobs_to_be_done.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No jobs defined</p>
                      )}
                    </div>

                    {/* Competitive Advantages */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Competitive Advantages
                      </h4>
                      {prop.competitive_advantages &&
                      prop.competitive_advantages.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {prop.competitive_advantages.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No advantages defined
                        </p>
                      )}
                    </div>

                    {/* Success Metrics */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Success Metrics
                      </h4>
                      {prop.success_metrics &&
                      prop.success_metrics.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {prop.success_metrics.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No metrics defined
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Types & Roles */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">User Types & Roles</h3>
                  {canEdit && (
                    <button
                      onClick={() => {
                        setEditingUserType(null);
                        setUserTypeForm({
                          role_type: '',
                          user_type: '',
                          description: '',
                          importance: '',
                        });
                        setShowUserTypeForm(true);
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Add User Type
                    </button>
                  )}
                </div>
                {prop.user_types_roles && prop.user_types_roles.length > 0 ? (
                  <div className="space-y-3">
                    {prop.user_types_roles.map((utr: any) => (
                      <div
                        key={utr.id}
                        className="border-l-4 border-blue-500 pl-4 relative group"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{utr.user_type}</span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {formatRoleType(utr.role_type)}
                          </span>
                          {utr.importance && (
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                utr.importance === 'critical'
                                  ? 'bg-red-100 text-red-700'
                                  : utr.importance === 'important'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {formatUserTypeImportance(utr.importance)}
                            </span>
                          )}
                        </div>
                        {utr.description && (
                          <p className="text-sm text-gray-600">
                            {utr.description}
                          </p>
                        )}
                        {canEdit && (
                          <div className="absolute top-0 right-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingUserType(utr);
                                setUserTypeForm({
                                  role_type: utr.role_type || '',
                                  user_type: utr.user_type || '',
                                  description: utr.description || '',
                                  importance: utr.importance || '',
                                });
                                setShowUserTypeForm(true);
                              }}
                              className="p-1 text-gray-600 hover:text-blue-600"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteUserType(utr.id)}
                              className="p-1 text-gray-600 hover:text-red-600"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No user types defined yet.
                  </p>
                )}
              </div>

              {/* Pricing Models */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Pricing Models</h3>
                  {canEdit && (
                    <button
                      onClick={() => {
                        setEditingPricing(null);
                        setPricingForm({
                          name: '',
                          description: '',
                          is_default: false,
                          price: '',
                          currency: '',
                          pricing_type: '',
                          frequency: '',
                          geographic_markets: [],
                          industry_verticals: [],
                        });
                        setShowPricingForm(true);
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Add Pricing Model
                    </button>
                  )}
                </div>
                {prop.pricing_models && prop.pricing_models.length > 0 ? (
                  <div className="space-y-3">
                    {prop.pricing_models.map((pm: any) => (
                      <div
                        key={pm.id}
                        className={`border rounded-lg p-4 relative group ${pm.is_default ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                      >
                        <div className="flex justify-between items-start mb-2 pr-16">
                          <h4 className="font-medium">{pm.name}</h4>
                          {pm.is_default && (
                            <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs">
                              Default
                            </span>
                          )}
                        </div>
                        {pm.description && (
                          <p className="text-sm text-gray-600 mb-2">
                            {pm.description}
                          </p>
                        )}
                        {pm.price && (
                          <p className="text-lg font-semibold">
                            {formatCurrencyUtil(pm.price, pm.currency || 'USD')}
                            {pm.frequency && (
                              <span className="text-sm font-normal text-gray-600">
                                {' '}
                                / {formatPricingFrequency(pm.frequency)}
                              </span>
                            )}
                          </p>
                        )}
                        {pm.pricing_type && (
                          <p className="text-sm text-gray-600">
                            Type: {formatPricingType(pm.pricing_type)}
                          </p>
                        )}
                        {canEdit && (
                          <div className="absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingPricing(pm);
                                setPricingForm({
                                  name: pm.name || '',
                                  description: pm.description || '',
                                  is_default: pm.is_default || false,
                                  price: pm.price?.toString() || '',
                                  currency: pm.currency || '',
                                  pricing_type: pm.pricing_type || '',
                                  frequency: pm.frequency || '',
                                  geographic_markets:
                                    pm.geographic_markets || [],
                                  industry_verticals:
                                    pm.industry_verticals || [],
                                });
                                setShowPricingForm(true);
                              }}
                              className="p-1 text-gray-600 hover:text-blue-600"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeletePricingModel(pm.id)}
                              className="p-1 text-gray-600 hover:text-red-600"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No pricing models defined yet.
                  </p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Financial Performance */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Financial Performance
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(prop.booked_business)}
                    </p>
                    <p className="text-sm text-gray-600">Booked Business</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(prop.lost_business)}
                    </p>
                    <p className="text-sm text-gray-600">Lost Business</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(prop.open_pipeline_unweighted)}
                    </p>
                    <p className="text-sm text-gray-600">Open Pipeline</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(prop.open_pipeline_weighted)}
                    </p>
                    <p className="text-sm text-gray-600">Weighted Pipeline</p>
                  </div>
                </div>
              </div>

              {/* Scoring Matrix */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Segment Scoring</h3>
                {prop.id && selectedOrg && (
                  <PropositionScoring
                    propositionId={prop.id}
                    organizationId={selectedOrg.id}
                    readOnly={!canEdit}
                  />
                )}
              </div>
            </div>
          </div>

          {/* User Type Form Dialog */}
          {showUserTypeForm && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-gray-500/75"
                  onClick={() => setShowUserTypeForm(false)}
                ></div>

                <div className="relative bg-white rounded-lg max-w-md w-full p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    {editingUserType
                      ? 'Edit User Type & Role'
                      : 'Add User Type & Role'}
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        User Type *
                      </label>
                      <input
                        type="text"
                        value={userTypeForm.user_type}
                        onChange={(e) =>
                          setUserTypeForm({
                            ...userTypeForm,
                            user_type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., End User, Administrator"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role Type *
                      </label>
                      <input
                        type="text"
                        value={userTypeForm.role_type}
                        onChange={(e) =>
                          setUserTypeForm({
                            ...userTypeForm,
                            role_type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Decision Maker, Influencer"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Importance
                      </label>
                      <select
                        value={userTypeForm.importance}
                        onChange={(e) =>
                          setUserTypeForm({
                            ...userTypeForm,
                            importance: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select importance</option>
                        <option value="critical">Critical</option>
                        <option value="important">Important</option>
                        <option value="nice-to-have">Nice to Have</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={userTypeForm.description}
                        onChange={(e) =>
                          setUserTypeForm({
                            ...userTypeForm,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe this user type's needs and characteristics..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowUserTypeForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveUserType}
                      disabled={
                        !userTypeForm.user_type || !userTypeForm.role_type
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {editingUserType ? 'Save Changes' : 'Add User Type'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Model Form Dialog */}
          {showPricingForm && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-gray-500/75"
                  onClick={() => setShowPricingForm(false)}
                ></div>

                <div className="relative bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                  <h2 className="text-xl font-semibold mb-4">
                    {editingPricing
                      ? 'Edit Pricing Model'
                      : 'Add Pricing Model'}
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={pricingForm.name}
                        onChange={(e) =>
                          setPricingForm({
                            ...pricingForm,
                            name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Professional Plan"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={pricingForm.description}
                        onChange={(e) =>
                          setPricingForm({
                            ...pricingForm,
                            description: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price
                        </label>
                        <input
                          type="number"
                          value={pricingForm.price}
                          onChange={(e) =>
                            setPricingForm({
                              ...pricingForm,
                              price: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency
                        </label>
                        <select
                          value={pricingForm.currency}
                          onChange={(e) =>
                            setPricingForm({
                              ...pricingForm,
                              currency: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">
                            Default ({organization?.currency || 'USD'})
                          </option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pricing Type
                        </label>
                        <select
                          value={pricingForm.pricing_type}
                          onChange={(e) =>
                            setPricingForm({
                              ...pricingForm,
                              pricing_type: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select type</option>
                          <option value="subscription">Subscription</option>
                          <option value="one_time">One-time</option>
                          <option value="usage_based">Usage-based</option>
                          <option value="tiered">Tiered</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Frequency
                        </label>
                        <select
                          value={pricingForm.frequency}
                          onChange={(e) =>
                            setPricingForm({
                              ...pricingForm,
                              frequency: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select frequency</option>
                          <option value="one_time">One-time</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annual">Annual</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={pricingForm.is_default}
                          onChange={(e) =>
                            setPricingForm({
                              ...pricingForm,
                              is_default: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Set as default pricing model
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowPricingForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePricingModel}
                      disabled={!pricingForm.name}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {editingPricing ? 'Save Changes' : 'Add Pricing Model'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}{' '}
      {/* Close overview tab */}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Tasks</h2>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setTaskViewMode(taskViewMode === 'kanban' ? 'list' : 'kanban')
                }
                className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                title={
                  taskViewMode === 'kanban'
                    ? 'Switch to List View'
                    : 'Switch to Kanban View'
                }
              >
                {taskViewMode === 'kanban' ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {taskViewMode === 'kanban' ? (
            <TaskKanbanView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="proposition"
              entityId={id}
              canEdit={canEdit}
              segments={[]}
              propositions={prop ? [prop] : []}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          ) : (
            <TaskListView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="proposition"
              entityId={id}
              canEdit={canEdit}
              segments={[]}
              propositions={prop ? [prop] : []}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          )}
        </div>
      )}
      {/* Research Tab */}
      {activeTab === 'research' && prop && selectedOrg && (
        <ResearchTab
          organizationId={selectedOrg.id}
          entityType={ResearchEntityType.PROPOSITION}
          entityId={prop.id}
          entityName={prop.name}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
